// react
// dependencies
import { useApolloClient } from "@apollo/client";
import React, { useEffect, useState } from "react";

import { faTriangleExclamation } from "@fortawesome/pro-solid-svg-icons";

import { PageBanner } from "@performyard/py-components";

import Button from "../../../../../components/Button";
import { Checkboxes } from "../../../../../components/Inputs/SelectionOptions/Checkboxes";
// components
import ContentModal from "../../../../../components/Modal/Templates/ContentModal";
import Typography from "../../../../../components/Text";

import useReportStore from "../../Report/store";
import { fetchSavedQuery, updateReportQuery } from "../../Report/store/requests";

import css from "../Home.module.scss";

type ShareReportModalProps = {
	handleClose: () => void;
	isOpen: boolean;
	report_id: string;
	sharedWithWho: string[];
	onSharedWithChange: (newSharedWith: string[]) => void;
	managersViewReportsSettingEnabled: boolean;
	contributorsViewReportSettingEnabled: boolean;
	groupAdminsViewReportsSettingEnabled: boolean;
};

export interface ReportVisibility {
	value: string;
	label: string;
}

const ShareReportModal = (props: ShareReportModalProps) => {
	const {
		handleClose,
		isOpen,
		report_id,
		sharedWithWho,
		onSharedWithChange,
		managersViewReportsSettingEnabled,
		contributorsViewReportSettingEnabled,
		groupAdminsViewReportsSettingEnabled,
	} = props;

	const [reportVisibility, setReportVisibility] = useState(
		sharedWithWho?.includes("managers") ||
			sharedWithWho?.includes("contributors") ||
			sharedWithWho?.includes("group_admins")
			? [
					{ value: "admins", label: "Admins" },
					...(sharedWithWho?.includes("managers") ? [{ value: "managers", label: "Managers" }] : []),
					...(sharedWithWho?.includes("contributors")
						? [{ value: "contributors", label: "Contributors" }]
						: []),
					...(sharedWithWho?.includes("group_admins")
						? [{ value: "group_admins", label: "Group Admins" }]
						: []),
				]
			: [{ value: "admins", label: "Admins" }]
	);
	const [currentReport, setCurrentReport] = useState<any>({});

	const client = useApolloClient();

	const [setClient, setReportID, report, sharedWith, setSharedWith, saveReport] = useReportStore((state) => [
		state.setClient,
		state.setReportID,
		state.report,
		state.sharedWith,
		state.setSharedWith,
		state.saveReport,
	]);

	useEffect(() => {
		setClient(client);
	}, [client]);

	useEffect(() => {
		const fetchReportData = async () => {
			setReportID(report_id);
			// fetch report data
			const response = await fetchSavedQuery(client, report_id);
			setCurrentReport(response);
		};

		setReportVisibility(
			sharedWithWho?.includes("managers") ||
				sharedWithWho?.includes("contributors") ||
				sharedWithWho?.includes("group_admins")
				? [
						{ value: "admins", label: "Admins" },
						...(sharedWithWho?.includes("managers") ? [{ value: "managers", label: "Managers" }] : []),
						...(sharedWithWho?.includes("contributors")
							? [{ value: "contributors", label: "Contributors" }]
							: []),
						...(sharedWithWho?.includes("group_admins")
							? [{ value: "group_admins", label: "Group Admins" }]
							: []),
					]
				: [{ value: "admins", label: "Admins" }]
		);

		fetchReportData();
	}, [report_id]);

	useEffect(() => {
		const selectedRoles = ["admins"];
		if (reportVisibility?.some((item) => item.value === "managers")) {
			selectedRoles.push("managers");
		}
		if (reportVisibility?.some((item) => item.value === "contributors")) {
			selectedRoles.push("contributors");
		}
		if (reportVisibility?.some((item) => item.value === "group_admins")) {
			selectedRoles.push("group_admins");
		}
		setSharedWith(selectedRoles);
	}, [reportVisibility]);

	const shareReport = async () => {
		if (!report) {
			const variables = {
				...currentReport,
				queryID: report_id,
				savedQuery: {
					type: currentReport?.type,
					name: currentReport?.name,
					description: currentReport?.description,
					tags: currentReport?.tags,
					conditions: currentReport?.filters,
				},
				sharedWith: sharedWith,
			};
			await updateReportQuery(client, variables);
		} else {
			saveReport();
		}
		onSharedWithChange(sharedWith);
		handleClose();
	};

	const shareReportModalOptions: { value: string; label: string }[] = [];

	if (managersViewReportsSettingEnabled) {
		shareReportModalOptions.push({ value: "managers", label: "Managers" });
	}
	if (contributorsViewReportSettingEnabled) {
		shareReportModalOptions.push({ value: "contributors", label: "Contributors" });
	}
	if (groupAdminsViewReportsSettingEnabled) {
		shareReportModalOptions.push({ value: "group_admins", label: "Group Admins" });
	}

	const genericWarningMessage = `Shared reports are view-only and pre-filtered based on the user's permissions and employee data field access.`;
	const shareReportModalSize = shareReportModalOptions?.length > 1 ? "large" : "small";

	return (
		<div className={css.shareReportModal}>
			<ContentModal
				isOpen={isOpen}
				size={shareReportModalSize}
				handleClose={handleClose}
				title={
					<div>
						<Typography size="paragraph-1" weight="semi-bold" text-align="center">
							Share Report With
						</Typography>
						<Typography color="neutral-9" size="paragraph-3" weight="regular" text-align="center">
							{genericWarningMessage}
						</Typography>
					</div>
				}
				buttons={[
					<Button
						key="share-report-back-button"
						color="blue"
						onClick={handleClose}
						data-testid={`share-report-back-button-${report_id}`}
					>
						Back
					</Button>,
					<Button
						key="share-report-save-button"
						data-testid={`share-report-save-button-${report_id}`}
						onClick={() => shareReport()}
						color="green"
					>
						Save
					</Button>,
				]}
			>
				{shareReportModalOptions?.length > 1 && (
					<div className={css.warningBanner}>
						<PageBanner
							icon={faTriangleExclamation}
							color="primary"
							mainTitle="If the report is shared with multiple roles and a user has many roles, access to employee data fields will be based on the role with the highest permission level."
						/>
					</div>
				)}
				<div className={css.checkBoxes}>
					<Checkboxes
						options={shareReportModalOptions}
						value={reportVisibility.filter((item) => item.value !== "admins")}
						onChange={(val) => {
							setReportVisibility([{ value: "admins", label: "Admins" }, ...val]);
						}}
						textSize={"paragraph-1"}
						checkboxLabelWeight={"regular"}
						gap={1}
					/>
				</div>
			</ContentModal>
		</div>
	);
};

export default ShareReportModal;
